import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {merge, ReplaySubject} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {WindowRefService} from '../../../services/window-ref.service';
import {destroy$} from '../../destroyable';
import {FlashMessageModule} from '../../flash-message/index';
import {L10nModule} from '../../translate/index';
import {ApplicationReloadService} from '../application-reload.service';

interface AppUpdateBubbleNotification {
    message: string;
    showReloadBtn?: boolean;
    showRetryBtn?: boolean;
    retryCallback?: () => void;
}

@Component({
    selector:        'app-updates',
    templateUrl:     './app-updates.component.html',
    styleUrls:       ['./app-updates.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports:         [
        L10nModule,
        FlashMessageModule
    ]
})
export class AppUpdatesComponent implements OnInit {

    public readonly bubbleNotification$ = new ReplaySubject<AppUpdateBubbleNotification>(1);

    private readonly windowRefService = inject(WindowRefService);
    private readonly applicationReloadService = inject(ApplicationReloadService);

    private readonly destroy$ = destroy$();

    public ngOnInit(): void {
        this.listenToUpdates();
        this.applicationReloadService.init();
    }

    public onReloadClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.windowRefService.nativeWindow.location.reload();
    }

    public onRetryClick(event: MouseEvent, callback: () => void): void {
        event.preventDefault();
        event.stopPropagation();

        callback();
    }

    private listenToUpdates(): void {
        merge(
            this.applicationReloadService.versionDetected$.pipe(
                tap(() => this.showVersionDetected())
            ),
            this.applicationReloadService.versionReady$.pipe(
                tap(() => this.showVersionReady())
            ),
            this.applicationReloadService.versionInstallationFailed$.pipe(
                tap(() => this.showVersionInstallationFailed())
            )
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private showVersionDetected(): void {
        this.bubbleNotification$.next({
            message: 'components.mobile.app-updates.new_version_available_to_download'
        });
    }

    private showVersionReady(): void {
        this.bubbleNotification$.next({
            message:       'components.mobile.app-updates.new_version_available_to_apply',
            showReloadBtn: true
        });
    }

    private showVersionInstallationFailed(): void {
        this.bubbleNotification$.next({
            message:       'components.mobile.app-updates.new_version_installation_failed',
            showRetryBtn:  true,
            retryCallback: () => this.applicationReloadService.forceCheckForUpdates()
        });
    }

}
